import { Alert, AnimatedContentLoader, Input, Select, TextArea } from '@root/components'
import { getContactPageUrl, getSettingsPageUrl, InvoiceDeliveryMethod } from '@root/constants'
import { isEmailDeliveryMethod } from './xrechnungDelivery'
import { useBusinessContext } from '@root/context'
import {
  fetchContact,
  fetchContactDeliveryMethods,
  fetchInvoiceDeliveryMethods,
  IInvoice,
  SendInvoiceProps
} from '@root/query'
import React, { useEffect } from 'react'
import { get, useFormContext, useWatch } from 'react-hook-form'
import { Trans, useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import styled from 'styled-components'

interface Props {
  invoiceReceiver: number
  invoiceId?: number
  emailContentName: 'data.email_content' | 'recurrence_email_content'
  emailSubjectName: 'data.email_subject' | 'recurrence_email_subject'
  changeDisabled?: (disabled: boolean) => void
  useEInvoicingIfAvailable?: boolean
}

const SendInvoiceFormBase: React.FC<Props> = ({
  invoiceReceiver: contactId,
  invoiceId,
  emailContentName,
  emailSubjectName,
  changeDisabled
}) => {
  const { slug } = useBusinessContext()
  const [t] = useTranslation()
  const {
    register,
    control,
    setValue,
    formState: { errors, isSubmitting }
  } = useFormContext<Partial<IInvoice> | SendInvoiceProps>()

  const {
    data: receiver,
    isLoading: isReceiverLoading,
    isError: isErrorReceiver
  } = useQuery({
    queryKey: [slug, 'contact', contactId],
    queryFn: () => fetchContact({ slug, contactId }),
    ...{
      enabled: contactId !== null && contactId !== undefined
    }
  })

  const {
    data: deliveryMethodOptions,
    isLoading: isDeliveryMethodsLoading,
    isError: isErrorDeliveryMethods
  } = useQuery({
    queryKey: invoiceId
      ? [slug, 'invoice', invoiceId, 'delivery_methods']
      : [slug, 'contact', contactId, 'delivery_methods'],
    queryFn: () =>
      invoiceId
        ? fetchInvoiceDeliveryMethods({ slug, invoiceId })
        : fetchContactDeliveryMethods({ slug, contactId: contactId }),
    ...{
      enabled:
        contactId !== null &&
        contactId !== undefined &&
        (invoiceId === undefined || invoiceId !== null)
    }
  })

  const isFormDataLoading = isReceiverLoading || isDeliveryMethodsLoading
  const isError = isErrorDeliveryMethods || isErrorReceiver

  const deliveryMethod = useWatch({
    control,
    name: 'delivery_method'
  })
  const isEmailDeliveryMethodSelected = isEmailDeliveryMethod(deliveryMethod)
  const selectedOption = deliveryMethodOptions?.find(({ method }) => method === deliveryMethod)

  useEffect(() => {
    const disabled =
      isFormDataLoading ||
      isError ||
      isSubmitting ||
      contactId === undefined ||
      contactId === null ||
      selectedOption?.errors?.length > 0
    changeDisabled?.(disabled)
  }, [isFormDataLoading, isError, isSubmitting, selectedOption])

  // Setting the default delivery method if no delivery method is selected on the form.
  // In the case where invoice is copied, don't owerwrite the delivery method!
  if (deliveryMethodOptions && deliveryMethodOptions.length > 0 && !deliveryMethod) {
    const einvoiceOption = deliveryMethodOptions.find(
      ({ method, errors }) => method === InvoiceDeliveryMethod.EINVOICE && errors.length === 0
    )
    const firstAvailableOption = deliveryMethodOptions.find(
      ({ method, errors }) => errors.length === 0 && method !== InvoiceDeliveryMethod.SELF_PRINT
    )

    if (einvoiceOption) {
      setValue('delivery_method', InvoiceDeliveryMethod.EINVOICE)
    } else if (firstAvailableOption) {
      setValue('delivery_method', firstAvailableOption.method)
    } else {
      setValue('delivery_method', deliveryMethodOptions[0].method)
    }
  }

  return (
    <>
      {isError ? (
        <AlertWrapper>
          <Alert
            type="error"
            description={t('invoicing.form.repetition.errorFetchingSubject')}
          ></Alert>
        </AlertWrapper>
      ) : contactId ? (
        <AnimatedContentLoader isLoading={isFormDataLoading}>
          <Select
            label={t('invoicing.form.repetition.header')}
            {...register('delivery_method', { shouldUnregister: true })}
          >
            {deliveryMethodOptions?.map(({ name, method }) => (
              <option key={method} value={method}>
                {name}
              </option>
            ))}
          </Select>

          {selectedOption?.errors?.map((error, index) => (
            <Alert key={`alert-${index}`} type="warning" description={error} />
          ))}

          {deliveryMethod === InvoiceDeliveryMethod.EMAIL_XRECHNUNG &&
            (selectedOption?.errors?.length ?? 0) > 0 && (
              <Alert
                type="info"
                description={
                  <Trans
                    i18nKey="invoicing.send.xrechnungRequirementsGuidance"
                    components={{
                      0: (
                        <Link
                          to={getSettingsPageUrl(slug, {
                            page: 'settings',
                            modal: 'business-identifiers'
                          })}
                        />
                      ),
                      1: <Link to={getSettingsPageUrl(slug, { page: 'invoicing-general' })} />,
                      2: <Link to={getContactPageUrl(slug, { id: contactId })} />
                    }}
                  />
                }
              />
            )}

          {selectedOption?.info && <Alert type="info" description={selectedOption?.info} />}

          {deliveryMethod === InvoiceDeliveryMethod.SELF_PRINT && (
            <Alert
              type="info"
              description={t(
                emailContentName === 'recurrence_email_content'
                  ? 'invoicing.send.selfPrintRecurrenceInfo'
                  : 'invoicing.send.selfPrintInfo'
              )}
            />
          )}

          {deliveryMethod === InvoiceDeliveryMethod.EINVOICE && (
            <>
              <Input
                label={t('invoicing.send.einvoiceAddress')}
                value={receiver?.invoicing_einvoice_address}
                disabled={true}
              />
              <Input
                label={t('invoicing.send.einvoiceOperator')}
                value={receiver?.invoicing_einvoice_operator}
                disabled={true}
              />
            </>
          )}

          {isEmailDeliveryMethodSelected && (
            <>
              <Input
                label={t('invoicing.send.receiver')}
                value={receiver?.invoicing_email}
                disabled={true}
              />
              <Input
                label={t('invoicing.send.emailSubject')}
                required={true}
                {...register(emailSubjectName, {
                  required: {
                    value: true,
                    message: t('validation.required')
                  },
                  shouldUnregister: true
                })}
                errors={get(errors, emailSubjectName)}
              />
              <TextArea
                label={t('invoicing.send.emailContent')}
                {...register(emailContentName, {
                  shouldUnregister: true
                })}
                errors={get(errors, emailContentName)}
              />
            </>
          )}
        </AnimatedContentLoader>
      ) : (
        <AlertWrapper>
          <Alert type={'warning'} description={t('invoicing.form.repetition.noSubject')}></Alert>
        </AlertWrapper>
      )}
    </>
  )
}

export default SendInvoiceFormBase

const AlertWrapper = styled.div`
  flex: 1 0 100%;
`
